import { Link, useLocation } from 'react-router-dom';
import { Package, LayoutGrid, Settings, FileText, LogOut, Menu, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';

const Layout = ({ user, children }) => {
  const location = useLocation();
  const [refreshing, setRefreshing] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  const handleRefreshConnection = async () => {
    try {
      setRefreshing(true);
      const response = await axiosInstance.post('/auth/refresh');
      
      if (response.data.ok) {
        toast.success('Connection refreshed!');
      }
    } catch (error) {
      console.error('Refresh error:', error);
      
      if (error.response?.status === 401) {
        toast.error('Session expired. Please reconnect in Settings.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to refresh connection');
      }
    } finally {
      setRefreshing(false);
    }
  };

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutGrid },
    { path: '/products', label: 'Products', icon: Package },
    { path: '/categories', label: 'Categories', icon: LayoutGrid },
    { path: '/catalog', label: 'Catalog', icon: FileText },
    { path: '/navigation', label: 'Navigation Builder', icon: Menu },
    { path: '/settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-200 shadow-sm z-10">
        <div className="p-6">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            ProductFlow
          </h1>
          <p className="text-sm text-slate-500 mt-1">Manage your inventory</p>
        </div>

        <nav className="px-3 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-indigo-50 text-indigo-600 font-medium'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-700">{user?.email}</p>
              <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              title="Logout"
            >
              <LogOut size={18} className="text-slate-600" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-64 p-8">
        {/* Header with Refresh Button */}
        <div className="max-w-7xl mx-auto mb-4 flex justify-end">
          <button
            onClick={handleRefreshConnection}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
            title="Refresh ExcaliburCRM Connection"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Refreshing...' : 'Refresh Connection'}
          </button>
        </div>
        
        <div className="max-w-7xl mx-auto">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
